import React from 'react';

import { type FilterResultItem } from '@neptune/shared/search-domain';

import { AttributeDefinition } from 'domain/experiment/attribute';

type UseAttributeInstantSubmitConfig<TAttribute extends AttributeDefinition> = {
  query: string;
  entries: FilterResultItem<TAttribute>[];
  createFallbackValue: (attributeName: string) => TAttribute;
  onSubmit?: (value: TAttribute) => void;
  enabled?: boolean;
};

export function useAttributeInstantSubmit<TAttribute extends AttributeDefinition>({
  query,
  entries,
  createFallbackValue,
  onSubmit,
  enabled = true,
}: UseAttributeInstantSubmitConfig<TAttribute>) {
  const valueRef = React.useRef(query);

  React.useEffect(() => {
    valueRef.current = query.trim();
  }, [query]);

  const getValueToSubmit = React.useCallback(() => {
    if (!enabled || valueRef.current.length === 0) {
      return undefined;
    }

    const exactMatch = entries.find((entry) => entry.original.name === valueRef.current)?.original;

    return exactMatch ?? createFallbackValue(valueRef.current);
  }, [enabled, createFallbackValue, entries]);

  const submit = React.useCallback(() => {
    const valueToSubmit = getValueToSubmit();

    if (!valueToSubmit) {
      return;
    }

    onSubmit?.(valueToSubmit);
  }, [getValueToSubmit, onSubmit]);

  const handleShortcutKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (!(event.metaKey || event.ctrlKey) || event.key !== 'Enter') {
        return;
      }

      if (!enabled || valueRef.current.length === 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      submit();
    },
    [enabled, submit],
  );

  const setValue = React.useCallback((value: string) => {
    valueRef.current = value.trim();
  }, []);

  return {
    submit,
    setValue,
    handleShortcutKeyDown,
  };
}
